import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ChevronLeft, Volume2, BookOpen, MessageCircle, HelpCircle,
  CheckCircle, XCircle, Star, Trophy, ArrowRight,
  RefreshCw, Play, Pause, Globe
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Activity types
const ACTIVITIES = [
  { id: "vocabulary", name: "Vocabulaire", icon: BookOpen, color: "green", emoji: "📚" },
  { id: "translation", name: "Phrases", icon: MessageCircle, color: "blue", emoji: "💬" },
  { id: "audio", name: "Prononciation", icon: Volume2, color: "purple", emoji: "🔊" },
  { id: "quiz", name: "Quiz", icon: HelpCircle, color: "orange", emoji: "❓" },
];

// Vocabulary categories
const CATEGORIES = [
  { id: "animals", name: "Animaux", emoji: "🐾" },
  { id: "colors", name: "Couleurs", emoji: "🎨" },
  { id: "numbers", name: "Nombres", emoji: "🔢" },
  { id: "food", name: "Nourriture", emoji: "🍎" },
  { id: "family", name: "Famille", emoji: "👨‍👩‍👧" },
  { id: "body", name: "Corps", emoji: "🦶" },
];

function FlashcardView({ category, onBack }) {
  const { user } = useAuth();
  const [words, setWords] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWords = async () => {
      try {
        const res = await fetch(`${API_URL}/api/english/vocabulary/${category}`, {
          headers: { "X-User-Id": user?.id || "" },
        });
        const data = await res.json();
        setWords(data.words || []);
      } catch (err) {
        console.error("Failed to fetch vocabulary:", err);
      }
      setLoading(false);
    };
    fetchWords();
  }, [category, user]);

  const currentWord = words[currentIndex];

  const nextCard = () => {
    setFlipped(false);
    setCurrentIndex((prev) => (prev + 1) % words.length);
  };

  const prevCard = () => {
    setFlipped(false);
    setCurrentIndex((prev) => (prev - 1 + words.length) % words.length);
  };

  // Simple TTS using browser API
  const speakWord = (text, lang = "en-US") => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang;
    utterance.rate = 0.8;
    speechSynthesis.speak(utterance);
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 border-4 border-green-200 border-t-green-500 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-slate-500">Chargement...</p>
      </div>
    );
  }

  if (!currentWord) return null;

  return (
    <div className="max-w-md mx-auto">
      {/* Progress */}
      <div className="flex items-center justify-between mb-6">
        <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-xl">
          <ChevronLeft size={24} className="text-slate-600" />
        </button>
        <span className="text-sm font-bold text-slate-500">
          {currentIndex + 1} / {words.length}
        </span>
      </div>

      {/* Flashcard */}
      <div
        onClick={() => setFlipped(!flipped)}
        className="relative h-64 cursor-pointer perspective-1000"
      >
        <div className={`absolute inset-0 transition-transform duration-500 preserve-3d ${flipped ? "rotate-y-180" : ""}`}>
          {/* Front (English) */}
          <div className={`absolute inset-0 bg-gradient-to-br from-green-400 to-emerald-500 rounded-3xl shadow-xl p-6 flex flex-col items-center justify-center backface-hidden ${flipped ? "invisible" : ""}`}>
            <span className="text-6xl mb-4">{currentWord.emoji}</span>
            <h2 className="text-3xl font-bold text-white">{currentWord.en}</h2>
            <button
              onClick={(e) => { e.stopPropagation(); speakWord(currentWord.en); }}
              className="mt-4 p-3 bg-white/20 rounded-full hover:bg-white/30"
            >
              <Volume2 className="text-white" size={24} />
            </button>
            <p className="text-sm text-white/70 mt-4">Appuie pour voir la traduction</p>
          </div>

          {/* Back (French) */}
          <div className={`absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-3xl shadow-xl p-6 flex flex-col items-center justify-center backface-hidden rotate-y-180 ${!flipped ? "invisible" : ""}`}>
            <span className="text-6xl mb-4">{currentWord.emoji}</span>
            <h2 className="text-3xl font-bold text-white">{currentWord.fr}</h2>
            <p className="text-lg text-white/80 mt-2">{currentWord.en}</p>
            <p className="text-sm text-white/70 mt-4">Appuie pour retourner</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-center gap-4 mt-8">
        <button
          onClick={prevCard}
          className="px-6 py-3 bg-slate-100 hover:bg-slate-200 rounded-xl font-bold text-slate-700"
        >
          Precedent
        </button>
        <button
          onClick={nextCard}
          className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl font-bold"
        >
          Suivant
        </button>
      </div>
    </div>
  );
}

function ExerciseView({ type, category, onBack }) {
  const { user } = useAuth();
  const [exercise, setExercise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userAnswer, setUserAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [startTime, setStartTime] = useState(null);

  const fetchExercise = useCallback(async () => {
    setLoading(true);
    setUserAnswer(null);
    setShowResult(false);
    setStartTime(Date.now());
    
    try {
      const res = await fetch(`${API_URL}/api/english/exercise`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User-Id": user?.id || "",
        },
        body: JSON.stringify({
          exercise_type: type,
          niveau: user?.niveau || "cp",
          category: category,
        }),
      });
      const data = await res.json();
      setExercise(data);
    } catch (err) {
      console.error("Failed to fetch exercise:", err);
    }
    setLoading(false);
  }, [type, category, user]);

  useEffect(() => {
    fetchExercise();
  }, [fetchExercise]);

  const speakWord = (text) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "en-US";
    utterance.rate = 0.8;
    speechSynthesis.speak(utterance);
  };

  const handleAnswer = async (answer) => {
    const answerValue = typeof answer === "object" ? answer.word : answer;
    setUserAnswer(answerValue);
    const isCorrect = answerValue.toLowerCase() === exercise.answer.toLowerCase();
    setShowResult(true);
    
    setScore(prev => ({
      correct: prev.correct + (isCorrect ? 1 : 0),
      total: prev.total + 1,
    }));

    // Save result
    try {
      const timeSpent = Math.round((Date.now() - startTime) / 1000);
      await fetch(`${API_URL}/api/english/result`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User-Id": user?.id || "",
        },
        body: JSON.stringify({
          exercise_id: exercise.id,
          correct: isCorrect,
          time_seconds: timeSpent,
          answer_given: answerValue,
        }),
      });
    } catch (err) {
      console.error("Failed to save result:", err);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-slate-500">Chargement...</p>
      </div>
    );
  }

  if (!exercise) return null;

  const isCorrect = userAnswer?.toLowerCase() === exercise.answer.toLowerCase();

  return (
    <div className="max-w-lg mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-xl">
          <ChevronLeft size={24} className="text-slate-600" />
        </button>
        <div className="flex items-center gap-2 bg-yellow-100 px-4 py-2 rounded-xl">
          <Trophy className="text-yellow-500" size={20} />
          <span className="font-bold text-yellow-700">{score.correct}/{score.total}</span>
        </div>
      </div>

      {/* Question */}
      <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8">
        <div className="text-center mb-6">
          {exercise.emoji && (
            <span className="text-5xl block mb-4">{exercise.emoji}</span>
          )}
          <h2 className="text-xl md:text-2xl font-bold text-slate-800">
            {exercise.question}
          </h2>
          
          {type === "audio" && exercise.word && (
            <button
              onClick={() => speakWord(exercise.word)}
              className="mt-4 p-4 bg-purple-100 hover:bg-purple-200 rounded-full mx-auto"
            >
              <Volume2 className="text-purple-600" size={32} />
            </button>
          )}
        </div>

        {/* Options */}
        {exercise.options && (
          <div className="grid grid-cols-2 gap-3">
            {exercise.options.map((option, i) => {
              const optValue = typeof option === "object" ? option.word : option;
              const optEmoji = typeof option === "object" ? option.emoji : null;
              const isSelected = userAnswer === optValue;
              const isCorrectOption = optValue.toLowerCase() === exercise.answer.toLowerCase();
              
              let bgColor = "bg-slate-100 hover:bg-slate-200";
              let borderColor = "border-transparent";
              
              if (showResult) {
                if (isCorrectOption) {
                  bgColor = "bg-green-100";
                  borderColor = "border-green-400";
                } else if (isSelected && !isCorrectOption) {
                  bgColor = "bg-red-100";
                  borderColor = "border-red-400";
                }
              } else if (isSelected) {
                bgColor = "bg-blue-100";
                borderColor = "border-blue-400";
              }
              
              return (
                <button
                  key={i}
                  onClick={() => !showResult && handleAnswer(option)}
                  disabled={showResult}
                  className={`${bgColor} border-2 ${borderColor} rounded-2xl p-4 text-center transition-all hover:scale-105 active:scale-95 disabled:hover:scale-100`}
                >
                  {optEmoji && <span className="text-3xl block mb-1">{optEmoji}</span>}
                  <span className="font-bold text-slate-700">{optValue}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Result */}
        {showResult && (
          <div className={`mt-6 p-4 rounded-2xl text-center ${isCorrect ? "bg-green-50" : "bg-red-50"}`}>
            {isCorrect ? (
              <div className="flex items-center justify-center gap-2 text-green-600">
                <Star className="fill-yellow-400 text-yellow-400" size={28} />
                <span className="text-xl font-bold">Well done!</span>
                <Star className="fill-yellow-400 text-yellow-400" size={28} />
              </div>
            ) : (
              <div className="text-red-600">
                <p className="font-bold text-lg">Try again!</p>
                <p className="text-sm mt-1">La bonne reponse etait : <strong>{exercise.answer}</strong></p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Next button */}
      {showResult && (
        <div className="flex justify-center mt-8">
          <button
            onClick={fetchExercise}
            className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transition-all hover:scale-105 flex items-center gap-2"
          >
            Next <ArrowRight size={20} />
          </button>
        </div>
      )}
    </div>
  );
}

export default function EnglishPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [view, setView] = useState("menu"); // menu, categories, flashcards, exercise
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Main menu
  if (view === "menu") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
        <header className="bg-white shadow-sm">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
            <button
              onClick={() => navigate("/home")}
              className="p-2 hover:bg-slate-100 rounded-xl"
            >
              <ChevronLeft size={24} className="text-slate-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <Globe className="text-green-500" size={28} /> English
              </h1>
              <p className="text-sm text-slate-500">Niveau {user?.niveau?.toUpperCase() || "CP"}</p>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-8">
          <h2 className="text-xl font-bold text-slate-700 mb-6">Choose an activity</h2>
          
          <div className="grid grid-cols-2 gap-4">
            {ACTIVITIES.map((activity) => (
              <button
                key={activity.id}
                onClick={() => {
                  setSelectedActivity(activity.id);
                  if (activity.id === "vocabulary") {
                    setView("categories");
                  } else {
                    setView("exercise");
                  }
                }}
                className={`bg-white rounded-2xl shadow-lg p-6 text-center hover:shadow-xl transition-all hover:scale-105 active:scale-95`}
              >
                <div className={`w-16 h-16 bg-${activity.color}-100 rounded-2xl flex items-center justify-center mx-auto mb-3`}>
                  <span className="text-4xl">{activity.emoji}</span>
                </div>
                <h3 className="font-bold text-slate-800">{activity.name}</h3>
              </button>
            ))}
          </div>
        </main>
      </div>
    );
  }

  // Category selection for vocabulary
  if (view === "categories") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
        <header className="bg-white shadow-sm">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
            <button
              onClick={() => setView("menu")}
              className="p-2 hover:bg-slate-100 rounded-xl"
            >
              <ChevronLeft size={24} className="text-slate-600" />
            </button>
            <h1 className="text-xl font-bold text-slate-800">Vocabulaire</h1>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-8">
          <h2 className="text-lg font-bold text-slate-700 mb-4">Choisis une categorie</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => {
                  setSelectedCategory(cat.id);
                  setView("flashcards");
                }}
                className="bg-white rounded-2xl shadow-lg p-5 text-center hover:shadow-xl transition-all hover:scale-105"
              >
                <span className="text-4xl block mb-2">{cat.emoji}</span>
                <h3 className="font-bold text-slate-700">{cat.name}</h3>
              </button>
            ))}
          </div>
        </main>
      </div>
    );
  }

  // Flashcards view
  if (view === "flashcards") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-8 px-4">
        <FlashcardView 
          category={selectedCategory} 
          onBack={() => setView("categories")} 
        />
      </div>
    );
  }

  // Exercise view
  if (view === "exercise") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-8 px-4">
        <ExerciseView 
          type={selectedActivity}
          category={selectedCategory}
          onBack={() => setView("menu")} 
        />
      </div>
    );
  }

  return null;
}
